import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';

// Types pour les données du parcours
interface ParcoursItem {
  id: number;
  title: string;
  organization: string;
  description: string;
  startDate: string;
  endDate: string;
  category: string;
  location: string;
}

// Définition des types pour les marqueurs d'années et les événements positionnés
interface YearMarker {
  year: number;
  month: number;
  label: string;
  position: number;
}

interface PositionedEvent extends ParcoursItem {
  position: number;
  width: number;
  above: boolean;
}

const ParcoursContainer = styled.div`
  padding: 8rem 2rem 6rem;
  min-height: calc(100vh - 14rem);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const TimelineContainer = styled.div`
  background-color: ${({ theme }) => `${theme.colors.background}cc`};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative;
  margin-bottom: 2rem;
`;

const TimelineLegend = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 2rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  
  span {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 0.5rem;
  }
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TimelineTrack = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow-x: auto;
  padding: 0 20px;
  
  /* Masquer la barre de défilement tout en gardant la fonctionnalité */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE et Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari et Opera */
  }
`;

const NavButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: 5px;' : 'right: 5px;'}
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const EventsRow = styled.div`
  position: relative;
  height: 100%;
  width: max-content;
  min-width: 100%;
`;

const EventBox = styled(motion.div)<{ isAbove: boolean, category: string }>`
  position: absolute;
  ${({ isAbove }) => isAbove ? 'top: 2px;' : 'bottom: 2px;'} /* Rapproché de l'axe (était à 5px) */
  cursor: pointer;
  height: 80px;
  border-radius: 8px;
  z-index: 5;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}ee`};
  backdrop-filter: blur(4px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    transform: translateY(${({ isAbove }) => isAbove ? '-3px' : '3px'});
    box-shadow: ${({ theme }) => theme.shadows.md};
    z-index: 6;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${({ category }) => category === 'education' ? '#9C5BB5' : '#1976D2'}; /* Remplacé par un bleu plus clair */
  }
`;

// Ajouter un composant pour le badge de date formaté
const FormattedDateBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  z-index: 10;
`;

const EventContent = styled.div`
  padding: 8px 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const EventTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0;
  white-space: normal; /* Permet le retour à la ligne (était nowrap) */
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limite à 2 lignes maximum */
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text};
`;

const EventSubtitle = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EventDate = styled.div`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  justify-content: space-between;
`;

const DetailCard = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}ee`};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  margin-top: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  h3 {
    font-size: 1.5rem;
    margin: 0 0 0.5rem 0;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const DetailOrganization = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.5rem;
`;

const DetailLocation = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const DetailTimePeriod = styled.div`
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${({ theme }) => `${theme.colors.background}aa`};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.5rem;
  cursor: pointer;
  margin-left: 1rem;
  padding: 0.2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    background-color: ${({ theme }) => `${theme.colors.background}88`};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const TagContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const Tag = styled.span<{ category: string }>`
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  background-color: ${({ category, theme }) => 
    category === 'education' ? `${theme.colors.primary}22` : `${theme.colors.accent}22`};
  color: ${({ category, theme }) => 
    category === 'education' ? theme.colors.primary : theme.colors.accent};
  border: 1px solid ${({ category, theme }) => 
    category === 'education' ? `${theme.colors.primary}44` : `${theme.colors.accent}44`};
`;

const calculateDuration = (startDate: string, endDate: string, t: any): string => {
  // Format des dates "MM-YYYY" au lieu de "YYYY-MM"
  const parseDate = d3.timeParse('%m-%Y');
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  // Vérifier si les dates sont valides
  if (!start || !end) {
    console.warn(`Erreur de parsing de date: ${startDate} - ${endDate}`);
    return t('common:duration.unknown');
  }
  
  const diffMs = end.getTime() - start.getTime();
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.floor(diffMs / msPerDay);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const weeks = Math.floor(days / 7);
  
  if (years > 0) {
    if (remainingMonths > 0) {
      return `${years} ${years > 1 ? t('common:duration.years') : t('common:duration.year')} ${remainingMonths} ${t('common:duration.months')}`;
    } else {
      return `${years} ${years > 1 ? t('common:duration.years') : t('common:duration.year')}`;
    }
  } else if (months > 0) {
    return `${months} ${t('common:duration.months')}`;
  } else if (weeks > 0) {
    return `${weeks} ${weeks > 1 ? t('common:duration.weeks') : t('common:duration.week')}`;
  } else {
    return `${days} ${days > 1 ? t('common:duration.days') : t('common:duration.day')}`;
  }
};

// Fonction pour formater les dates selon la langue et la taille d'écran
const formatDate = (dateString: string, language: string, isMobile = false): string => {
  // Format des dates stockées "MM-YYYY"
  const [month, year] = dateString.split('-');
  
  if (language === 'fr') {
    // Format compact pour mobile: MM/YYYY
    if (isMobile) {
      return `${month}/${year}`;
    }
    
    // Format français standard: mois année (exemple: "janvier 2024")
    const monthNames = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    // Soustraire 1 car les mois sont stockés de 01 à 12, mais les indices du tableau vont de 0 à 11
    const monthIndex = parseInt(month, 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthNames[monthIndex]} ${year}`;
    } else {
      return `${month}/${year}`; // Fallback
    }
  } else {
    // Format anglais: YYYY/MM
    return `${year}/${month}`;
  }
};

const ParcoursPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<ParcoursItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [timelineWidth, setTimelineWidth] = useState(1000);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [timelineData, setTimelineData] = useState<{
    positionedEvents: PositionedEvent[],
    years: YearMarker[],
    todayPosition: number
  }>({ positionedEvents: [], years: [], todayPosition: 0 });
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>(new Date().toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US'));
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Détection de la largeur de l'écran
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Fonction pour déterminer si l'affichage doit être compact
  const isCompactView = useCallback(() => {
    return windowWidth < 768; // Mobile breakpoint
  }, [windowWidth]);

  // Mise à jour de l'heure en temps réel chaque seconde
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US'));
    }, 1000);
    
    // Nettoyage de l'intervalle lors du démontage du composant
    return () => clearInterval(intervalId);
  }, [i18n.language]);

  const parcoursData: ParcoursItem[] = [
    {
      id: 1,
      title: t('career:items.1.title'),
      organization: t('career:items.1.organization'),
      description: t('career:items.1.description'),
      startDate: "01-2024",
      endDate: "09-2025",
      category: "professional",
      location: t('career:items.1.location')
    },
    {
      id: 2,
      title: t('career:items.2.title'),
      organization: t('career:items.2.organization'),
      description: t('career:items.2.description'),
      startDate: "03-2022",
      endDate: "04-2022",
      category: "professional",
      location: t('career:items.2.location')
    },
    {
      id: 3,
      title: t('career:items.3.title'),
      organization: t('career:items.3.organization'),
      description: t('career:items.3.description'),
      startDate: "10-2016",
      endDate: "10-2019",
      category: "professional",
      location: t('career:items.3.location')
    },
    {
      id: 4,
      title: t('career:items.4.title'),
      organization: t('career:items.4.organization'),
      description: t('career:items.4.description'),
      startDate: "09-2023",
      endDate: "09-2025",
      category: "education",
      location: t('career:items.4.location')
    },
    {
      id: 6,
      title: t('career:items.6.title'),
      organization: t('career:items.6.organization'),
      description: t('career:items.6.description'),
      startDate: "09-2019",
      endDate: "06-2022",
      category: "education",
      location: t('career:items.6.location')
    },
    {
      id: 7,
      title: t('career:items.7.title'),
      organization: t('career:items.7.organization'),
      description: t('career:items.7.description'),
      startDate: "03-2016",
      endDate: "09-2016",
      category: "education",
      location: t('career:items.7.location')
    },
    {
      id: 8,
      title: t('career:items.8.title'),
      organization: t('career:items.8.organization'),
      description: t('career:items.8.description'),
      startDate: "03-2023",
      endDate: "12-2023",
      category: "professional",
      location: t('career:items.8.location')
    },
    {
      id: 9,
      title: t('career:items.9.title'),
      organization: t('career:items.9.organization'),
      description: t('career:items.9.description'),
      startDate: "07-2022",
      endDate: "01-2023",
      category: "professional",
      location: t('career:items.9.location')
    },
    {
      id: 10,
      title: t('career:items.10.title'),
      organization: t('career:items.10.organization'),
      description: t('career:items.10.description'),
      startDate: "06-2021",
      endDate: "09-2021",
      category: "professional",
      location: t('career:items.10.location')
    }
  ];
  
  const calculateTimelinePositions = useCallback(() => {
    const parseDate = d3.timeParse('%m-%Y');
    const today = new Date(); // Date du jour pour la ligne verticale

    const startDates = parcoursData.map(item => parseDate(item.startDate) as Date);
    const endDates = parcoursData.map(item => parseDate(item.endDate) as Date);
    
    // Vérifier si toutes les dates sont valides
    if (startDates.some((d: Date | null) => !d) || endDates.some((d: Date | null) => !d)) {
      console.warn("Erreur: certaines dates n'ont pas pu être analysées correctement");
      return { positionedEvents: [], years: [], calculatedWidth: 1000, todayPosition: 0 };
    }
    
    // On filtre les dates nulles par sécurité
    const validStartDates = startDates.filter((d): d is Date => d !== null);
    const validEndDates = endDates.filter((d): d is Date => d !== null);
    
    if (validStartDates.length === 0 || validEndDates.length === 0) {
      return { positionedEvents: [], years: [], calculatedWidth: 1000, todayPosition: 0 };
    }
    
    const minDate = d3.min(validStartDates) as Date;
    const maxDate = d3.max(validEndDates) as Date;

    const adjustedMinDate = new Date(minDate);
    adjustedMinDate.setMonth(adjustedMinDate.getMonth() - 3);

    const adjustedMaxDate = new Date(maxDate);
    adjustedMaxDate.setMonth(adjustedMaxDate.getMonth() + 3);

    const baseWidth = Math.max(window.innerWidth * 1.5, 1500);

    const timeScale = d3.scaleTime()
      .domain([adjustedMinDate, adjustedMaxDate])
      .range([100, baseWidth - 100]);

    // Calculer la position de la date du jour
    let todayPosition = 0;
    if (today >= adjustedMinDate && today <= adjustedMaxDate) {
      todayPosition = timeScale(today);
    } else if (today < adjustedMinDate) {
      todayPosition = timeScale(adjustedMinDate);
    } else {
      todayPosition = timeScale(adjustedMaxDate);
    }

    // Trier les événements par date de début pour traiter d'abord les plus anciens
    const sortedParcoursData = [...parcoursData].sort((a, b) => {
      const startDateA = parseDate(a.startDate);
      const startDateB = parseDate(b.startDate);
      if (!startDateA || !startDateB) return 0;
      return startDateA.getTime() - startDateB.getTime();
    });

    // Suivre les boxes déjà positionnées pour éviter les chevauchements
    const professionalBoxesEndPositions: number[] = [];
    const educationBoxesEndPositions: number[] = [];

    // Positionner les événements
    const positionedEvents: PositionedEvent[] = sortedParcoursData.map(item => {
      const startDate = parseDate(item.startDate);
      const endDate = parseDate(item.endDate);
      
      // Vérifier si les dates sont valides
      if (!startDate || !endDate) {
        // Retourner un événement avec des valeurs par défaut
        return {
          ...item,
          position: 0,
          width: 150,
          above: item.category === 'professional'
        };
      }
      
      let position = timeScale(startDate);
      // La largeur est basée sur la différence entre les dates
      let width = Math.max(150, timeScale(endDate) - timeScale(startDate));
      
      // Vérifier les chevauchements et ajuster
      const isAbove = item.category === 'professional';
      const existingEndPositions = isAbove ? professionalBoxesEndPositions : educationBoxesEndPositions;
      
      // Chercher le dernier box qui pourrait chevaucher
      for (let i = 0; i < existingEndPositions.length; i++) {
        const endPos = existingEndPositions[i];
        // Si la position de début est inférieure à la fin d'un box existant + marge
        // alors on décale la box vers la droite
        if (position < endPos + 10) {
          position = endPos + 10;
          // Ajuster la largeur pour ne pas dépasser la date de fin réelle
          const maxPosition = timeScale(endDate);
          if (position + width > maxPosition) {
            width = Math.max(150, maxPosition - position);
          }
        }
      }
      
      // Ajouter la position de fin au tableau
      existingEndPositions.push(position + width);
      
      return {
        ...item,
        position,
        width,
        above: isAbove
      };
    });

    const years: YearMarker[] = [];
    let currentYear = adjustedMinDate.getFullYear();

    while (currentYear <= adjustedMaxDate.getFullYear()) {
      const yearDate = new Date(currentYear, 0, 1);

      if (yearDate >= adjustedMinDate && yearDate <= adjustedMaxDate) {
        years.push({
          year: currentYear,
          month: 0,
          label: currentYear.toString(),
          position: timeScale(yearDate)
        });
      }
      currentYear++;
    }

    // Calculer la largeur totale nécessaire en prenant en compte les boxes ajustées
    const maxEndPosition = Math.max(
      ...positionedEvents.map(event => event.position + event.width),
      ...years.map(y => y.position)
    );
    const calculatedWidth = maxEndPosition + 150;

    return { positionedEvents, years, calculatedWidth, todayPosition };
  }, [parcoursData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const { positionedEvents, years, calculatedWidth, todayPosition } = calculateTimelinePositions();
      setTimelineData({ positionedEvents, years, todayPosition });
      setTimelineWidth(calculatedWidth);
      setLoading(false);

      requestAnimationFrame(() => {
        if (timelineRef.current) {
          const container = timelineRef.current;
          const eventsRow = container.querySelector('div[style*="width"]') as HTMLElement;
          if (eventsRow && eventsRow.style.width) {
            const axisContainer = container.querySelector('div[class^="TimelineAxisContainer"]') as HTMLElement;
            if (axisContainer) {
              axisContainer.style.width = eventsRow.style.width;
              axisContainer.style.minWidth = eventsRow.style.width;
            }

            if (!initialScrollDone) {
              setTimeout(() => {
                const maxScroll = container.scrollWidth - container.clientWidth;
                container.scrollTo({
                  left: maxScroll,
                  behavior: 'smooth'
                });
                setScrollPosition(maxScroll);
                setInitialScrollDone(true);
              }, 100);
            }
          }
        }
      });
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [calculateTimelinePositions, initialScrollDone]);

  const scrollTimeline = (direction: 'left' | 'right') => {
    if (timelineRef.current) {
      const container = timelineRef.current;
      const scrollAmount = container.clientWidth * 0.8;

      const newScrollPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);

      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });

      setScrollPosition(newScrollPosition);
    }
  };

  const handleScroll = () => {
    if (timelineRef.current) {
      setScrollPosition(timelineRef.current.scrollLeft);
    }
  };

  useEffect(() => {
    const container = timelineRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const scrollToEvent = (position: number) => {
    if (timelineRef.current) {
      const newScrollPosition = position - timelineRef.current.clientWidth / 2;
      timelineRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
      setScrollPosition(newScrollPosition);
    }
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = timelineRef.current 
    ? scrollPosition < (timelineRef.current.scrollWidth - timelineRef.current.clientWidth - 10)
    : false;

  return (
    <ParcoursContainer>
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('career:pageTitle')}
      </SectionTitle>
      
      <TimelineLegend>
        <LegendItem>
          <span style={{ backgroundColor: '#9C5BB5' }}></span>
          {t('career:legend.education')}
        </LegendItem>
        <LegendItem>
          <span style={{ backgroundColor: '#1976D2' }}></span>
          {t('career:legend.professional')}
        </LegendItem>
      </TimelineLegend>
      
      <ChronologyContainer>
        {/* Version desktop - Timeline horizontale */}
        <TimelineDesktopContainer>
          <TimelineContainer>
            {loading ? (
              <Loading>{t('career:loading')}</Loading>
            ) : (
              <>
                <NavButton
                  direction="left" 
                  onClick={() => scrollTimeline('left')}
                  disabled={!canScrollLeft}
                  aria-label="Défiler vers la gauche"
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                  </svg>
                </NavButton>
                
                <TimelineTrack 
                  ref={timelineRef}
                >
                  <EventsRow style={{ width: `${timelineWidth}px` }}>
                    {/* Barre horizontale de la timeline */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        width: `${timelineWidth}px`,
                        height: '2px',
                        backgroundColor: '#5a5a5a',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                      }}
                    />
                    
                    {/* Marqueurs d'années avec graduations */}
                    {timelineData.years.map((yearMarker, index) => (
                      <React.Fragment key={`${yearMarker.year}-${yearMarker.month}`}>
                        {/* Graduation verticale */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '46%',
                            left: `${yearMarker.position}px`,
                            height: '8%',
                            width: '1px',
                            backgroundColor: '#aaaaaa',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                          }}
                        />
                        {/* Étiquette d'année */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '40%',
                            left: `${yearMarker.position}px`,
                            transform: 'translateX(-50%)',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            color: '#f5f5f5',
                            padding: '1px 5px',
                            borderRadius: '3px',
                            zIndex: 5,
                          }}
                        >
                          {yearMarker.label}
                        </div>
                      </React.Fragment>
                    ))}
                    
                    {/* Ligne verticale pour la date du jour */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '0',
                        left: `${timelineData.todayPosition}px`,
                        height: '100%',
                        width: '2px',
                        backgroundColor: '#FF5722',
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                      }}
                    />
                    {/* Étiquette indiquant "Aujourd'hui" avec la date précise - placée en bas */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '120px',
                        left: `${timelineData.todayPosition}px`,
                        transform: 'translateX(-50%)',
                        backgroundColor: '#FF5722',
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        zIndex: 4,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: '0.65rem', marginTop: '2px' }}>
                        {new Date().toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                      </span>
                      <span style={{ fontSize: '0.70rem', marginTop: '2px', fontFamily: 'monospace' }}>
                        {currentTime}
                      </span>
                    </div>
                    
                    {/* Boxes d'événements positionnées des deux côtés de l'axe */}
                    {timelineData.positionedEvents.map((event) => (
                      <EventBox
                        key={event.id}
                        initial={{ opacity: 0, y: event.above ? -20 : 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        style={{
                          left: `${event.position}px`,
                          width: `${event.width}px`,
                        }}
                        isAbove={event.above}
                        category={event.category}
                        onClick={() => {
                          setSelectedItem(event);
                          scrollToEvent(event.position + event.width / 2);
                        }}
                      >
                        <EventContent>
                          <div>
                            <EventTitle>{event.title}</EventTitle>
                            <EventSubtitle>{event.organization}</EventSubtitle>
                          </div>
                          <EventDate>
                            <span>{calculateDuration(event.startDate, event.endDate, t)}</span>
                          </EventDate>
                        </EventContent>
                      </EventBox>
                    ))}
                  </EventsRow>
                </TimelineTrack>
                
                <NavButton 
                  direction="right" 
                  onClick={() => scrollTimeline('right')}
                  disabled={!canScrollRight}
                  aria-label="Défiler vers la droite"
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                  </svg>
                </NavButton>
              </>
            )}
          </TimelineContainer>
        </TimelineDesktopContainer>
        
        {/* Version mobile - Timeline verticale */}
        <MobileTimelineContainer>
          {loading ? (
            <Loading>{t('career:loading')}</Loading>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Affichage des événements en liste verticale */}
              <div style={{ position: 'relative', paddingLeft: '1rem' }}>
                {/* Ligne verticale continue */}
                <div
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '2px',
                    height: '100%',
                    backgroundColor: '#5a5a5a',
                    zIndex: 1,
                  }}
                />
                
                {/* Affichage des événements en liste verticale */}
                {parcoursData
                  .sort((a, b) => {
                    // Trier par date de début (la plus récente en premier)
                    const dateA = d3.timeParse('%m-%Y')(a.startDate);
                    const dateB = d3.timeParse('%m-%Y')(b.startDate);
                    if (!dateA || !dateB) return 0;
                    return dateB.getTime() - dateA.getTime();
                  })
                  .map((item, index) => (
                    <VerticalTimelineItem
                      key={item.id}
                      category={item.category}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      onClick={() => setSelectedItem(item)}
                    >
                      <VerticalTimelineHeader>
                        <VerticalTimelineTitle>{item.title}</VerticalTimelineTitle>
                        <VerticalTimelineDate>
                          {formatDate(item.startDate, i18n.language, isCompactView())} - {formatDate(item.endDate, i18n.language, isCompactView())}
                        </VerticalTimelineDate>
                      </VerticalTimelineHeader>
                      
                      <VerticalTimelineOrg>{item.organization}</VerticalTimelineOrg>
                      
                      <VerticalTimelineLocation>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        {item.location}
                      </VerticalTimelineLocation>
                      
                      <VerticalTimelineDuration>
                        {calculateDuration(item.startDate, item.endDate, t)}
                      </VerticalTimelineDuration>
                      
                      <VerticalTimelineDesc>{item.description}</VerticalTimelineDesc>
                    </VerticalTimelineItem>
                  ))}
              </div>
            </motion.div>
          )}
        </MobileTimelineContainer>
        
        {/* Carte de détail affichée lorsqu'un élément est sélectionné (vue desktop seulement) */}
        <AnimatePresence>
          {selectedItem && window.innerWidth > 768 && (
            <DetailCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <DetailHeader>
                <div>
                  <h3>{selectedItem.title}</h3>
                  <DetailOrganization>{selectedItem.organization}</DetailOrganization>
                  <DetailLocation>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    {selectedItem.location}
                  </DetailLocation>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <DetailTimePeriod>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                    </svg>
                    <span style={{ display: 'inline-block', marginLeft: '5px' }}>
                      {formatDate(selectedItem.startDate, i18n.language, isCompactView())} - {formatDate(selectedItem.endDate, i18n.language, isCompactView())}
                      <span style={{ marginLeft: '8px', display: 'inline-block' }}>
                        ({calculateDuration(selectedItem.startDate, selectedItem.endDate, t)})
                      </span>
                    </span>
                  </DetailTimePeriod>
                  <CloseButton onClick={() => setSelectedItem(null)} aria-label="Fermer">
                    ×
                  </CloseButton>
                </div>
              </DetailHeader>
              
              <DetailDescription>{selectedItem.description}</DetailDescription>
            </DetailCard>
          )}
        </AnimatePresence>
      </ChronologyContainer>
    </ParcoursContainer>
  );
};

const ChronologyContainer = styled.div`
  max-width: 70%;
  margin: 0 auto 2rem;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

// Styles pour l'affichage mobile vertical de la timeline
const TimelineDesktopContainer = styled.div`
  display: none; /* Masqué par défaut */
  
  /* Commenté pour garder sous la main
  @media (max-width: 1920px) {
    display: none;
  }
  */
`;

const MobileTimelineContainer = styled.div`
  display: block; /* Affiché par défaut */
  margin-top: 2rem;
  
  /* Commenté pour garder sous la main
  display: none;
  
  @media (max-width: 1920px) {
    display: block;
    margin-top: 2rem;
  }
  */
`;

const VerticalTimelineItem = styled(motion.div)<{ category: string }>`
  position: relative;
  padding: 1.5rem 1rem 1.5rem 2rem;
  margin-bottom: 1.5rem;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}ee`};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-left: 4px solid ${({ category }) => 
    category === 'education' ? '#9C5BB5' : '#1976D2'};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 1.5rem;
    left: -10px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ${({ category }) => 
      category === 'education' ? '#9C5BB5' : '#1976D2'};
    border: 2px solid ${({ theme }) => theme.colors.background};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 2.4rem;
    bottom: -1.5rem;
    left: -2px;
    width: 2px;
    background-color: ${({ theme }) => theme.colors.border};
    z-index: -1;
  }
  
  &:last-child::after {
    display: none;
  }
`;

const VerticalTimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const VerticalTimelineTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const VerticalTimelineOrg = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.3rem;
`;

const VerticalTimelineDate = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  padding: 0.2rem 0.6rem;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 4px;
`;

const VerticalTimelineDesc = styled.p`
  font-size: 0.9rem;
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
`;

const VerticalTimelineLocation = styled.div`
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const VerticalTimelineDuration = styled.div`
  display: inline-block;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export default ParcoursPage;